import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useSelectedAccount } from '../../contexts/SelectedAccountContext';
import { BackButton, Button, LoadingButton, useToast } from '../../components/ui';

const exampleIdeas = [
  {
    id: 1,
    title: '分享個人經歷',
    description: '範例：今天在咖啡廳工作時，我發現了一個簡單的專注技巧...',
    content: '今天在咖啡廳工作時，我發現了一個簡單的專注技巧...'
  },
  {
    id: 2,
    title: '分享你的觀點',
    description: '範例：關於時間管理，我有一些不同的想法...',
    content: '關於時間管理，我有一些不同的想法...'
  },
  {
    id: 3,
    title: '職場洞察',
    description: '範例：在公司工作三年後，我學到最重要的一件事是...',
    content: '在公司工作三年後，我學到最重要的一件事是...'
  },
  {
    id: 4,
    title: '生活觀察',
    description: '範例：最近注意到一個有趣的現象，很多人都傾向於...',
    content: '最近注意到一個有趣的現象，很多人都傾向於...'
  }
];

// 內容類型選項
const contentTypes = [
  {
    id: 'image-text',
    name: '圖 + 文',
    description: '生成圖片配文字，適合視覺化內容',
    icon: 'mdi:image-text',
    color: '#10b981'
  },
  {
    id: 'text-only',
    name: '純文字',
    description: '直接生成社群貼文，快速發布',
    icon: 'mdi:text',
    color: '#3B82F6'
  }
];

// 文章長度選項
const lengthOptions = [
  {
    id: 'short',
    name: '短文',
    description: '200-300字，適合快速閱讀',
    color: '#3B82F6'
  },
  {
    id: 'long',
    name: '長文',
    description: '300-500字，深度內容',
    color: '#8B5CF6'
  }
];

function IdeaToPostPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedAccount } = useSelectedAccount();
  const [userInput, setUserInput] = useState('');
  const [contentType, setContentType] = useState('text-only');
  const [selectedLength, setSelectedLength] = useState('short');
  const [generatedPost, setGeneratedPost] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState(null); // 儲存創建的 Project ID

  const handleExampleClick = (example) => {
    setUserInput(example.content);
  };

  const getPrompt = (userIdea, personaSetting, length) => {
    const wordCount = length === 'short' ? '200-300' : '300-500';

    return `你是一位專業的內容創作者，專注於寫出具有「故事感 + 顛覆感 + 互動性」的社群貼文。
請根據以下規則，把用戶的「idea」發展成一篇 Threads / IG 適合的短文：

[輸出規則]
1. 字數限制：全文落在 ${wordCount} 字之間，保持緊湊。
2. 段落結構：全文切成 4–6 段，每段約 2–3 行。
3. 開頭規則：必須用一句吸睛開場，不超過 15 字。
4. 中段發展：用「鋪陳-反轉-思考」結構。
5. 結尾處理：不喊口號，留一句簡潔有力的 takeaway。
6. 語調：像朋友聊天，不做作，不說教。
7. 避免使用 emoji、hashtag。
8. 【重要】直接輸出文章內容，不要加任何標題、標籤、說明文字（如「吸引人的開場：」「**」等），不要用 markdown 格式。

<用戶的想法>
${userIdea}

<我的人物設定>
${personaSetting}`;
  };

  const handleNext = async () => {
    if (!userInput.trim()) {
      toast.warning('請輸入你的想法或選擇範例');
      return;
    }

    if (!contentType) {
      toast.warning('請選擇內容類型');
      return;
    }

    // 如果選擇圖+文，創建 Project 到 Supabase 並跳轉到 step2
    if (contentType === 'image-text') {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          toast.error('請先登入');
          navigate('/login');
          return;
        }

        // 創建 Project 到 Supabase
        const projectData = {
          name: `Idea to Post - ${userInput.substring(0, 30)}...`,
          type: 'content-generator',
          template: 'idea-to-post',
          source: 'idea-to-post',
          topic: userInput.substring(0, 100),
          contentType: 'carousel',
          pageCount: 10,
          language: 'zh-TW',
          currentStep: 1,
          generationStatus: 'draft',
          settings: {
            articleLength: selectedLength,
            preset: 'default'
          },
          selectedContent: {
            idea: userInput
          }
        };

        const response = await fetch('/api/projects/create', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(projectData)
        });

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || '創建專案失敗');
        }

        const projectId = result.project.id;

        // 使用 URL 參數導航到 Step 2
        navigate(`/templates/content-generator/step2?projectId=${projectId}`);
      } catch (error) {
        console.error('創建專案失敗:', error);
        toast.error(error.message || '創建專案失敗，請稍後再試');
      }
      return;
    }

    // 如果選擇純文字，先創建 Project 再生成
    setIsGenerating(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('未登入或登入已過期，請重新登入');
      }

      // 1. 先創建 Project
      const projectData = {
        name: `Idea to Post - ${userInput.substring(0, 30)}...`,
        type: 'idea-to-post',
        template: 'idea-to-post',
        contentType: 'text',
        currentStep: 1,
        generationStatus: 'generating',
        settings: {
          length: selectedLength,
          source: 'idea-to-post',
          originalIdea: userInput
        }
      };

      const createResponse = await fetch('/api/projects/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(projectData)
      });

      const createResult = await createResponse.json();

      if (!createResult.success) {
        throw new Error(createResult.error || '創建專案失敗');
      }

      const projectId = createResult.project.id;
      setCurrentProjectId(projectId);
      console.log('✅ Project created on Idea to Post (text):', projectId);

      // 2. 構建 prompt 並生成內容
      const audience = selectedAccount?.settings?.audience || '';
      const topic = selectedAccount?.settings?.topic || '';
      const contentTask = selectedAccount?.settings?.content_task || '';
      const personaSetting = `受眾：${audience}\n主題：${topic}\n內容任務：${contentTask}`;

      const prompt = getPrompt(userInput, personaSetting, selectedLength);
      const maxTokens = selectedLength === 'short' ? 500 : 800;

      // 調用 AI API
      const response = await fetch('/api/ai/generate-content', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: prompt,
          maxTokens: maxTokens
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('登入已過期，請重新登入');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || '生成失敗');
      }

      const data = await response.json();

      // 處理生成的內容
      let content = data.content || data.text || '';
      content = content
        .replace(/。」/g, '」\n')
        .replace(/。(?!\n)/g, '。\n')
        .replace(/\n\n+/g, '\n\n')
        .trim();

      // 3. 更新 Project 加入生成的內容
      await fetch(`/api/projects/${projectId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          generatedImages: [{
            title: '',
            description: content,
            isFirstPage: true
          }],
          currentStep: 4,
          generationStatus: 'pending'
        })
      });

      setGeneratedPost({
        content: content,
        length: selectedLength,
        timestamp: new Date().toISOString()
      });

      toast.success('文章生成成功！');
    } catch (error) {
      console.error('Generate error:', error);
      toast.error(error.message || '生成失敗，請稍後再試');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = () => {
    if (generatedPost?.content) {
      navigator.clipboard.writeText(generatedPost.content);
      toast.success('已複製到剪貼簿');
    }
  };

  const handleRegenerate = () => {
    setGeneratedPost(null);
    handleNext();
  };

  const handlePublish = async () => {
    if (!generatedPost?.content) return;

    // 確認已有 Project（由 handleNext 時創建）
    if (!currentProjectId) {
      toast.error('專案未找到，請重新生成');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('請先登入');
        navigate('/login');
        return;
      }

      // 更新 Project 的生成內容（如果用戶有編輯）
      await fetch(`/api/projects/${currentProjectId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          generatedImages: [{
            title: '',
            description: generatedPost.content,
            isFirstPage: true
          }]
        })
      });

      // 使用 URL 參數導航到 Step 4
      navigate(`/templates/content-generator/step4?projectId=${currentProjectId}`);

    } catch (error) {
      console.error('發布失敗:', error);
      toast.error(error.message || '發布失敗，請稍後再試');
    }
  };

  const charCount = userInput.length;
  const isValid = userInput.trim().length > 0;

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-8">
      {/* Back Button */}
      <BackButton
        onClick={() => navigate('/home')}
        className="fixed top-8 left-8 z-[100]"
      >
        返回
      </BackButton>

      {/* Main Content Box */}
      <div className="max-w-[980px] w-full bg-white rounded-none p-12 shadow-none">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-[2.5rem] font-bold text-black mb-2">Idea to Post</h1>
          <p className="text-base text-gray-600">分享你的想法，轉化為吸引人的貼文</p>
        </div>

        {/* Input Section */}
        <div className="mb-8">
          <div className="text-sm font-semibold text-slate-800 mb-4">你想分享什麼？</div>
          <textarea
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            placeholder="在這裡輸入你的想法、觀點或經歷..."
            className="w-full min-h-[200px] p-5 border border-gray-200 rounded-lg text-base resize-y font-sans mb-2 leading-relaxed focus:outline-none focus:border-slate-400"
          />
          <div className="text-right text-xs text-slate-400 mt-2 mb-6">
            {charCount} 字
          </div>
        </div>

        {/* Example Ideas */}
        <div className="mb-8">
          <div className="text-sm font-semibold text-slate-800 mb-4">需要靈感？試試這些範例：</div>
          <div className="grid grid-cols-2 gap-4 mb-8">
            {exampleIdeas.map((example) => (
              <div
                key={example.id}
                onClick={() => handleExampleClick(example)}
                className="p-5 bg-slate-50 border border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:border-black hover:bg-white hover:-translate-y-0.5"
              >
                <div className="text-sm font-semibold text-slate-800 mb-2 flex items-center">
                  <Icon icon="mdi:lightbulb-on-outline" className="mr-2 text-base" />
                  {example.title}
                </div>
                <div className="text-xs text-slate-500 leading-relaxed">
                  {example.description}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Length Selection */}
        <div className="mb-8">
            <div className="text-sm font-semibold text-slate-800 mb-4">選擇文章長度</div>
            <div className="grid grid-cols-2 gap-4 mb-8">
              {lengthOptions.map((option) => {
                const isSelected = selectedLength === option.id;
                return (
                  <div
                    key={option.id}
                    onClick={() => setSelectedLength(option.id)}
                    className={`p-5 rounded-lg cursor-pointer transition-all duration-200 text-center ${
                      isSelected
                        ? 'border-2'
                        : 'border border-gray-200 bg-slate-50 hover:-translate-y-0.5'
                    }`}
                    style={{
                      borderColor: isSelected ? option.color : undefined,
                      background: isSelected ? `${option.color}10` : undefined
                    }}
                  >
                    <div
                      className="text-sm font-semibold mb-1"
                      style={{ color: isSelected ? option.color : '#1e293b' }}
                    >
                      {option.name}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {option.description}
                    </div>
                  </div>
                );
              })}
            </div>
        </div>

        {/* Next/Generate Button */}
        <LoadingButton
          onClick={handleNext}
          disabled={!isValid}
          loading={isGenerating}
          loadingText="生成中..."
          icon="mdi:sparkles"
          variant="primary"
          size="lg"
          className="w-full"
        >
          生成貼文
        </LoadingButton>

        {/* Generated Result */}
        {generatedPost && (
          <div className="mt-12 p-8 bg-slate-50 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <Icon icon="mdi:check-circle" className="text-emerald-500 text-xl" />
                生成的貼文
              </div>
              <div className="text-xs text-slate-400">
                {generatedPost.content.length} 字
              </div>
            </div>
            <div className="text-base text-slate-700 leading-loose whitespace-pre-wrap">
              {generatedPost.content}
            </div>
            <div className="flex gap-4 mt-6">
              <Button
                onClick={handleCopy}
                variant="secondary"
                size="md"
                icon="mdi:content-copy"
                className="flex-1"
              >
                複製
              </Button>
              <Button
                onClick={handleRegenerate}
                variant="secondary"
                size="md"
                icon="mdi:refresh"
                className="flex-1"
              >
                重新生成
              </Button>
              <Button
                onClick={handlePublish}
                variant="accent"
                size="md"
                icon="mdi:send"
                className="flex-[1.5]"
              >
                發布到 Threads
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default IdeaToPostPage;
